import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import * as Actions from 'actions'
import Button from 'components/base/Button'
import MenuItem from 'components/base/MenuItem'
import Select from 'components/base/Select'
import { TranslationContext } from 'contexts/TranslationContext'
import React, { useCallback, useContext, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import Scrollbars from '~/components/base/Scrollbars'
import { ReactComponent as CloseIcon } from '~/assets/settings/navigation_close.svg'
import { TASK_STATUS, HIEROGLYPH_LANG } from '~/enums'
import { useAction } from '~/hooks'
import './Transcribe.scss'
import { getLang } from '~/helpers/getLang'
import { useOpenProject } from '~/components/Projects/hooks/useProjectItemProps/useOpenProject'
import { useOnEscClose } from '~/hooks/modal/useOnEscClose'
import { Status2Text } from '~/Util/status'


function Transcribe() {
  const hideTranscribeSettings = useAction(Actions.mainView.showTranscribeSettings, false)
  const { t } = useContext(TranslationContext)
  const { taskId, fileId, fileName, transcribeModels,
    status, error, progress, result } = useSelector(state => state.transcribe)

  const initalModelName = transcribeModels && transcribeModels.length ? transcribeModels[0] : ''
  const [ modelName, setModelName ] = useState(initalModelName)
  const requestCreateTask = useAction(Actions.transcribe.requestCreateTask,
    fileId, modelName)
  const deleteTask = useAction(Actions.transcribe.requestDeleteTask, taskId)
  const clearTask = useAction(Actions.transcribe.clearTask)
  const fileNameWithoutExt = fileName?.replace(/\.[^.]+$/, '') || ''
  const projectName = `${t('TRANSCRIBE_PROJECT_POSTFIX')}_${fileNameWithoutExt}`
  const createProject = useAction(
    Actions.transcribe.requestCreateProjects, projectName, fileId, result
  )

  const openProject = useOpenProject()

  const deletingTaskRef = useRef(false)
  const createTaskRef = useRef(false)
  const createProjectRef = useRef(false)

  const onChangeModel = useCallback(e => {
    const modelName = e.target.value
    setModelName(modelName)
  }, [ setModelName ])


  const onSubmit = useCallback(async () => {
    if (createTaskRef.current) {
      return
    }
    createTaskRef.current = true
    try {
      await requestCreateTask()
    } finally {
      createTaskRef.current = false
    }
  }, [ requestCreateTask ])

  const onCancel = useCallback(async () => {
    if (deletingTaskRef.current) {
      return
    }
    deletingTaskRef.current = true
    try {
      await deleteTask()
      hideTranscribeSettings()
    } finally {
      deletingTaskRef.current = false
    }
  }, [ deleteTask, hideTranscribeSettings ])

  const [ confirm, setConfirm ] = React.useState(false)
  const onCloseClick = useCallback(() => {
    if (status && status !== TASK_STATUS.COMPLETED && status !== TASK_STATUS.ABORTED) {
      setConfirm(true)
    } else {
      hideTranscribeSettings()
      clearTask()
    }
  }, [ status, hideTranscribeSettings, setConfirm, clearTask ])

  const onConfirmationCanceled = useCallback(() => {
    setConfirm(false)
  }, [])

  const onConfirm = useCallback(async () => {
    if (deletingTaskRef.current) {
      return
    }
    deletingTaskRef.current = true
    try {
      await deleteTask()
      hideTranscribeSettings()
    } finally {
      deletingTaskRef.current = false
    }
  }, [ hideTranscribeSettings, deleteTask ])

  const onCreateProject = useCallback(async () => {
    if (createProjectRef.current) {
      return
    }
    createProjectRef.current = true
    try {
      const projectId = await createProject()
      hideTranscribeSettings()
      clearTask()
      await openProject(projectId)
    } finally {
      createProjectRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ createProject, hideTranscribeSettings, clearTask ])
  useOnEscClose({ onClose: onCloseClick })
  return (
    <div className="transcribe-settings allow-draggable">
      <div className="transcribe-settings__head allow-draggable">
        <div className="transcribe-settings__top allow-draggable">
          <span className="transcribe-settings__caption allow-draggable">
            {t('TRANSCRIBE_CAPTION')}
          </span>
          <div className="transcribe-settings__close" onClick={onCloseClick}>
            <CloseIcon />
          </div>
          {confirm && <Confirmation onCancel={onConfirmationCanceled} onClose={onConfirm} />}
        </div>
        <If condition={!status}>
          <Controls
            modelName={modelName}
            transcribeModels={transcribeModels}
            onChangeModel={onChangeModel}
            onSubmit={onSubmit}
          />
        </If>
        <If condition={status && status !== TASK_STATUS.COMPLETED}>
          <ControlsStatus
            onCancel={onCancel}
            status={status}
            error={error}
            progress={progress}
          />
        </If>
        <If condition={status && status === TASK_STATUS.COMPLETED}>
          <Info result={result} onCreateProject={onCreateProject} />
        </If>
      </div>
    </div>
  )
}

function Controls({ transcribeModels,
  modelName,
  onChangeModel,
  onSubmit }) {
  const { t } = useContext(TranslationContext)
  return (
    <div className="transcribe-settings__controls">
      <div className="transcribe-settings__params">
        <div className="transcribe-settings__onnx">
          <span className="transcribe-settings__label">{t('AI_MODEL')}</span>
          <Select
            value={modelName || (transcribeModels.length ? transcribeModels[0] : '')}
            onChange={onChangeModel}
          >
            {transcribeModels.map((name, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <MenuItem key={`transcribe_${index}`} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <div className="transcribe-settings__btn-start">
        <Button
          primary
          onClick={onSubmit}
          data-lang-id="NABLET_HLD_DETECTED_BTN_START"
        >
          {t('NABLET_HLD_DETECTED_BTN_START')}
        </Button>
      </div>
    </div>
  )
}

function ControlsStatus({ onCancel, status, error, progress }) {
  const { t } = useContext(TranslationContext)
  const showProgressCancel = (status
    && status !== TASK_STATUS.COMPLETED
    && status !== TASK_STATUS.ABORTED)


  function status2TextTranscribe(status, error, t) {
    switch (status) {
      case TASK_STATUS.RUNNING:
        return `${t('TRANSCRIBE_STATUS_TEXT_TRANSCRIBING')}...`
      case TASK_STATUS.ABORTED: {
        return `${t('COMMON_STATUS_TEXT_ABORTED')}: ${error.message}`
      }
      default:
        return Status2Text(status, error, t)
    }
  }

  const statusText = status2TextTranscribe(status, error, t)

  return (
    <>
      <div className="transcribe-settings__status">
        <If condition={showProgressCancel}>
          <div className="transcribe-settings__loader">
            <div className="transcribe-settings__loader-inner" style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
          <div className="transcribe-settings__btn-cancel">
            <Button className={HIEROGLYPH_LANG.includes(getLang()) ? 'fs15' : ''} onClick={onCancel} secondary>{t('NABLET_HLD_DETECTED_BTN_CANCEL')}</Button>
          </div>
        </If>

      </div>
      <StatusText text={statusText} />
    </>
  )
}

function StatusText({ text }) {
  return (
    <div className="transcribe-settings__text">
      <p>{text}</p>
    </div>
  )
}

function Info({ result, onCreateProject }) {
  const { t } = useContext(TranslationContext)
  const itemsCount = result ? result.length : 0
  return (
    <>
      {itemsCount > 0 && (
        <div className="transcribe-settings__btn-create-project">
          <Button
            primary
            onClick={onCreateProject}
            data-lang-id="TRANSCRIBE_BTN_CREATE_PROJECT"
          >
            {t('TRANSCRIBE_BTN_CREATE_PROJECT')}
          </Button>
        </div>
      )}
      <Scrollbars className="scrollbar" style={{ height: '110px' }}>
        {/* List each label and its intervals count */}
        <ul className="transcribe-settings__info">
          <li className="transcribe-settings__info-item">
            {t('TRANSCRIBED_TEXT_ITEMS_COUNT')}
            <span>{itemsCount}</span>
          </li>
        </ul>
      </Scrollbars>
    </>
  )
}

function Confirmation({ onCancel, onClose }) {
  const { t } = useContext(TranslationContext)
  return (
    <Dialog disableBackdropClick open onClose={onCancel}>
      <DialogContent>
        <DialogContentText>
          {t('NABLET_HLD_DETECTED_CLOSE_CONFIRMATION')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button primary onClick={onCancel} data-lang-id="NABLET_HLD_DETECTED_BTN_NO">
          {t('NABLET_HLD_DETECTED_BTN_NO')}
        </Button>
        <Button primary onClick={onClose} autoFocus data-lang-id="NABLET_HLD_DETECTED_BTN_YES">
          {t('NABLET_HLD_DETECTED_BTN_YES')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default Transcribe
